import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Box, Paper } from '@mui/material';
import type { AiChatProps, AiChatHandle, AiChatStyleProps } from './AiChatTypes';
import { AiChatProvider } from './AiChatProvider';
import { useAiChatServices } from './AiChatContext';
import ChatPane from './ChatPane/ChatPane';

const AiChatInner = forwardRef<AiChatHandle, AiChatStyleProps>(
    ({ bordered, borderRadius, elevation, onToolEvent, bottomSlot, children }, ref) => {
        // `sendMessage` is owned by the provider (single source of truth) and exposed on the
        // context; the imperative ref handle just re-exports it so both entry points behave
        // identically (including the `{ ui }` payload handling).
        const { sessionStore, eventBusService, sendMessage } = useAiChatServices();
        const onToolEventRef = useRef(onToolEvent);
        onToolEventRef.current = onToolEvent;

        useImperativeHandle(ref, () => ({
            sendMessage,
            getMessages: () => sessionStore.getState().messages,
            getSession: () => sessionStore.getState().session,
            getConnectionState: () => sessionStore.getState().connectionStatus,
        }), [sendMessage, sessionStore]);

        useEffect(() => {
            const handler = async (eventName: string, eventData: string) => {
                onToolEventRef.current?.(eventName, eventData);
            };
            eventBusService.gatewayOnToolEvent(handler);
            return () => {
                eventBusService.gatewayOffToolEvent(handler);
            };
        }, [eventBusService]);

        const hasStyling = bordered || borderRadius || elevation;

        // ChatPane uses `height: 100%` and pins its prompt at the bottom
        // via internal flex column. To insert a `bottomSlot` *below* the
        // prompt we wrap both in a flex column where ChatPane takes
        // `flex: 1` (so it still fills the available space and its
        // internal scroll boundary works) and the slot lives below it
        // as a `flex: 0 0 auto` row. The wrapper itself takes `height: 100%`
        // so it inherits the host's sizing the same way the bare
        // ChatPane used to.
        const content = children !== undefined ?
                children :
                    bottomSlot ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <ChatPane />
                            </Box>
                            {bottomSlot}
                        </Box>
                    ) :
                    (
                        <ChatPane />
                    );

        if (!hasStyling) {
            return content;
        }

        return (
            <Paper
                variant={bordered ? 'outlined' : 'elevation'}
                elevation={bordered ? 0 : (elevation ?? 0)}
                color='black'

                sx={{ backgroundColor: 'black', borderRadius: borderRadius ?? 0, height: '100%', overflow: 'hidden' }}
            >
                {content}
            </Paper>
        );
    }
);

AiChatInner.displayName = 'AiChatInner';

const AiChat = forwardRef<AiChatHandle, AiChatProps>((props, ref) => {
    const { bordered, borderRadius, elevation, children, bottomSlot, ...providerProps } = props;

    return (
        <AiChatProvider {...providerProps}>
            <AiChatInner
                ref={ref}
                bordered={bordered}
                onToolEvent={props.onToolEvent}
                borderRadius={borderRadius}
                elevation={elevation}
                bottomSlot={bottomSlot}
            >
                {children}
            </AiChatInner>
        </AiChatProvider>
    );
});

AiChat.displayName = 'AiChat';

export default AiChat;
